import { lazy, Suspense, forwardRef } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import MarqueeText from "@/components/MarqueeText";

// Lazy load below-fold sections
const AboutPreview = lazy(() => import("@/components/AboutPreview"));
const CoffeeSelection = lazy(() => import("@/components/CoffeeSelection"));
const WhyChooseUs = lazy(() => import("@/components/WhyChooseUs"));
const B2BSection = lazy(() => import("@/components/B2BSection"));
const SolutionPreview = lazy(() => import("@/components/SolutionPreview"));
const GallerySection = lazy(() => import("@/components/GallerySection"));
const FAQPreview = lazy(() => import("@/components/FAQPreview"));
const NewsSection = lazy(() => import("@/components/NewsSection"));
const CTASection = lazy(() => import("@/components/CTASection"));
const ClientLogos = lazy(() => import("@/components/ClientLogos"));
const Footer = lazy(() => import("@/components/Footer"));

const homeBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Beranda",
      "item": "https://crestoracoffee.space"
    }
  ]
};

const homeFaqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Produk dan layanan apa yang Crestora Coffee sediakan?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Crestora Coffee Roasters menyediakan biji kopi panggang berkualitas tinggi (single origin & house blend), solusi mesin kopi (jual & sewa), konsultasi roastery, dan program Coffee Shop Starter untuk membantu Anda memulai bisnis kopi."
      }
    },
    {
      "@type": "Question",
      "name": "Bagaimana cara memesan kopi Crestora?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Anda dapat memesan kopi Crestora melalui WhatsApp di +62 823-1557-0277, email di hello@crestoracoffee.space, atau melalui formulir kontak di website kami."
      }
    },
    {
      "@type": "Question",
      "name": "Apakah Crestora Coffee menyediakan sampel gratis?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ya, kami menyediakan sampel gratis untuk calon mitra bisnis seperti kafe, restoran, dan hotel yang ingin mencoba kualitas biji kopi kami sebelum melakukan pemesanan dalam jumlah besar."
      }
    }
  ]
};

const homeOrganizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": "https://crestoracoffee.space/#organization",
  "name": "Crestora Coffee Roasters",
  "url": "https://crestoracoffee.space",
  "logo": "https://crestoracoffee.space/crestora-logo.png",
  "image": "https://storage.googleapis.com/gpt-engineer-file-uploads/JggLZGz0axOoNVHpKjwIQNuSn422/social-images/social-1767973685970-IMG_8647.JPG",
  "description": "Produsen & supplier biji kopi panggang premium di Yogyakarta. Menyediakan single origin, house blend, solusi mesin kopi kantor, konsultasi roastery, dan Coffee Shop Starter.",
  "telephone": "+62-823-1557-0277",
  "email": "hello@crestoracoffee.space",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Jl. Seturan Raya No.17A, Ngropoh, Condongcatur",
    "addressLocality": "Sleman",
    "addressRegion": "Daerah Istimewa Yogyakarta",
    "postalCode": "55283",
    "addressCountry": "ID"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "-7.7713",
    "longitude": "110.4098"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "09:00",
      "closes": "18:00"
    }
  ],
  "priceRange": "$$",
  "servesCuisine": "Coffee",
  "sameAs": [
    "https://instagram.com/CrestoraCoffee"
  ],
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.9",
    "reviewCount": "250",
    "bestRating": "5",
    "worstRating": "1"
  }
};

const SectionFallback = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((props, ref) => <div ref={ref} className="py-16" {...props} />);

const Index = () => {
  return (
    <>
      <Helmet>
        <title>Crestora Coffee Roasters | Premium Kopi Roastery Yogyakarta</title>
        <meta name="description" content="Crestora Coffee Roasters - Produsen & supplier biji kopi panggang premium di Yogyakarta. Single origin, house blend, solusi mesin kopi kantor, roastery, dan Coffee Shop Starter." />
        <link rel="canonical" href="https://crestoracoffee.space" />
        <script type="application/ld+json">{JSON.stringify(homeBreadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(homeFaqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(homeOrganizationJsonLd)}</script>
      </Helmet>
      <div className="min-h-screen">
        <Navbar />
        <main id="main-content">
          <HeroSection />
          <MarqueeText />
          <Suspense fallback={<SectionFallback />}>
            <AboutPreview />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <CoffeeSelection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <WhyChooseUs />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <B2BSection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <SolutionPreview />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <GallerySection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <FAQPreview />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <NewsSection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <CTASection />
          </Suspense>
          <Suspense fallback={<SectionFallback />}>
            <ClientLogos />
          </Suspense>
        </main>
        <Suspense fallback={<SectionFallback />}>
          <Footer />
        </Suspense>
      </div>
    </>
  );
};

export default Index;
